import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';

/**
 * HTTP client. Auth lives in httpOnly cookies set by the backend — no tokens in JS.
 * On 401 we call POST /auth/refresh once (concurrent requests share the same in-flight refresh),
 * then retry. If the refresh fails, the `onSessionExpired` handler runs (auth store → /login).
 */
export const API_BASE_URL: string = import.meta.env.VITE_API_URL ?? '/api/v1';

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

/** Żądania API w locie — automatyczna aktualizacja czeka, aż się uspokoją (przeładowanie w środku rotacji
 *  refresh-tokena wylogowuje, a urwany zapis wraca błędem). Każde żądanie, także ponowione po odświeżeniu sesji,
 *  liczy się raz: +1 przy wysyłce, −1 przy odpowiedzi albo błędzie. */
let inFlight = 0;
export const apiRequestsInFlight = (): number => inFlight;
const settle = (): void => {
  inFlight = Math.max(0, inFlight - 1);
};

apiClient.interceptors.request.use((config) => {
  inFlight += 1;
  return config;
});

/** Endpoints that must never trigger the refresh dance. */
const NO_REFRESH = ['/auth/login', '/auth/refresh', '/auth/logout', '/organization/branding'];

let refreshPromise: Promise<void> | null = null;
let sessionExpiredHandler: (() => void) | null = null;
let restrictionHandler: (() => void) | null = null;

const RESTRICTION_CODES = ['PASSWORD_CHANGE_REQUIRED', 'TWO_FACTOR_SETUP_REQUIRED'];

/** Called when the server refuses a call because the session is restricted (temporary password / required 2FA). */
export function onAccessRestricted(handler: () => void): void {
  restrictionHandler = handler;
}

export function onSessionExpired(handler: () => void): void {
  sessionExpiredHandler = handler;
}

function refreshSession(): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = apiClient
      .post('/auth/refresh')
      .then(() => undefined)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

apiClient.interceptors.response.use(
  (res) => {
    settle();
    return res;
  },
  async (error: AxiosError) => {
    settle();
    const original = error.config as RetriableConfig | undefined;
    const url = original?.url ?? '';
    const skip = NO_REFRESH.some((p) => url.startsWith(p));

    if (error.response?.status === 401 && original && !original._retry && !skip) {
      original._retry = true;
      try {
        await refreshSession();
      } catch (refreshError) {
        sessionExpiredHandler?.();
        return Promise.reject(error);
      }
      return apiClient(original);
    }
    const body = error.response?.data as { code?: string } | undefined;
    if (error.response?.status === 403 && body?.code && RESTRICTION_CODES.includes(body.code)) restrictionHandler?.();
    return Promise.reject(error);
  },
);

/** Accepts both raw bodies and a `{ success, data }` envelope (in case the backend wraps responses). */
function unwrap<T>(body: unknown): T {
  if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
    return (body as { data: T }).data;
  }
  return body as T;
}

export type QueryParams = Record<string, string | number | boolean | undefined | null>;

export const api = {
  get: async <T>(url: string, params?: QueryParams, config?: AxiosRequestConfig): Promise<T> =>
    unwrap<T>((await apiClient.get(url, { ...config, params })).data),
  post: async <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    unwrap<T>((await apiClient.post(url, data, config)).data),
  patch: async <T>(url: string, data?: unknown): Promise<T> => unwrap<T>((await apiClient.patch(url, data)).data),
  put: async <T>(url: string, data?: unknown): Promise<T> => unwrap<T>((await apiClient.put(url, data)).data),
  delete: async <T>(url: string): Promise<T> => unwrap<T>((await apiClient.delete(url)).data),
};

interface ApiErrorBody {
  code?: string;
  message?: string | string[];
  error?: { code?: string; message?: string | string[] } | string;
}

function errorBody(error: unknown): ApiErrorBody | undefined {
  return axios.isAxiosError(error) ? (error.response?.data as ApiErrorBody | undefined) : undefined;
}

export function getErrorStatus(error: unknown): number | undefined {
  return axios.isAxiosError(error) ? error.response?.status : undefined;
}

export function getErrorCode(error: unknown): string | undefined {
  const body = errorBody(error);
  if (!body) return undefined;
  return body.code ?? (typeof body.error === 'object' ? body.error.code : undefined);
}

/** Server-provided message, if any (network errors / generic failures return undefined). */
export function getErrorMessage(error: unknown): string | undefined {
  const body = errorBody(error);
  const msg = body?.message ?? (typeof body?.error === 'object' ? body.error.message : undefined);
  if (Array.isArray(msg)) return msg.join(', ');
  return msg || undefined;
}

/** Standard paginated list shape returned by list endpoints. */
export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}
